import { TextProps, AssetType, TextRef } from '../../../types/typing.ts';
import { InputField } from '../inputfield/InputField.ts';
import { Application, Graphics, Container, TextStyle, Rectangle } from 'pixi.js';
import React from 'react';
import { startRecording } from './recordAnimation.ts';

export const renderText = async (
  app: Application | null,
  selectedText: TextProps,
  onUpdateText: (id: string, properties: Partial<TextProps>) => void,
  onAssetSelection: (id: string, assetType: AssetType) => void,
  textRef: React.MutableRefObject<Map<string, TextRef>>,
  isRecordingRef: React.MutableRefObject<boolean | null | undefined>,
  recordingStartTimeRef: React.MutableRefObject<number | null | undefined>,
  selectedMenuRef: React.MutableRefObject<string | null | undefined>
) => {
  if (!app) return;

  try {
    const textInputFieldContainer = new Container();
    const bubbleBackground = new Graphics();
    const textInputBackground = new Graphics();
    const resizeHandle = new Graphics();

    const placeholderText = 'Enter text here';
    const initialText = selectedText.text?.trim() ?? '';
    let isPlaceholder = initialText === '';
    let isLocallySelected = false;
    let boxWidth = Math.max(180, Math.min(900, selectedText.boxWidth ?? 440));

    const style = new TextStyle({
      fontFamily: selectedText.fontProperties.fontFamily,
      fontSize: selectedText.fontProperties.fontSize,
      fontStyle: selectedText.fontProperties.fontStyle,
      fontWeight: selectedText.fontProperties.fontWeight,
      fill: isPlaceholder ? 0xaaaaaa : selectedText.fontProperties.fontColor,
      stroke: {
        color: isPlaceholder ? '#000000' : selectedText.fontProperties.fontColor,
        width: 1,
        join: 'round',
      },
      wordWrap: true,
      wordWrapWidth: boxWidth - 20,
    });

    const textInput = new InputField({
      text: isPlaceholder ? placeholderText : selectedText.text,
      style,
    });

    if (textInput.inputElement) {
      textInput.inputElement.value = isPlaceholder ? '' : selectedText.text;
    }

    textInputFieldContainer.addChild(bubbleBackground);
    textInputFieldContainer.addChild(textInputBackground);
    textInputFieldContainer.addChild(textInput);
    textInputFieldContainer.addChild(resizeHandle);

    textInputFieldContainer.x = selectedText.position.x;
    textInputFieldContainer.y = selectedText.position.y;

    textInputBackground.visible = false;
    bubbleBackground.visible = selectedText.styleType === 'bubble';
    resizeHandle.visible = false;

    const selectText = () => {
      textRef.current.forEach((ref) => {
        ref.isSelected = ref === textRefObject;
      });
      isLocallySelected = true;
      adjustTextBoxSize();
      onAssetSelection(textRefObject.id, AssetType.TEXT);
    };

    const startEditing = () => {
      textInputBackground.visible = true;
      if (isPlaceholder) {
        textInput.text = '';
        textInput.style.fill = selectedText.fontProperties.fontColor;
        isPlaceholder = false;
      }
      textInput.inputElement?.focus();
    };

    const stopEditing = () => {
      textInputBackground.visible = false;
      if (textInput.GetInputText().trim() === '') {
        textInput.text = placeholderText;
        textInput.style.fill = 0xaaaaaa;
        isPlaceholder = true;
        selectedText.text = '';
        onUpdateText(selectedText.id, { text: '' });
      } else {
        selectedText.text = textInput.text;
        onUpdateText(selectedText.id, { text: textInput.text });
      }
    };

    const restorePlaceholderIfEmpty = () => {
      if (textInput.GetInputText().trim() !== '') return;
      textInput.text = placeholderText;
      textInput.style.fill = 0xaaaaaa;
      isPlaceholder = true;
      selectedText.text = '';
      onUpdateText(selectedText.id, { text: '' });
    };

    const adjustTextBoxSize = () => {
      const padding = 10;
      const minWidth = 180;
      const maxWidth = 900;
      const inputText = textInput.GetInputText().trim();
      const words = inputText.length > 0 ? inputText.split(/\s+/) : [];

      // Keep user-defined width, but expand if a single long word cannot fit.
      const unwrappedStyle = new TextStyle({
        ...style,
        wordWrap: false,
      });
      const longestWordWidth = words.reduce((max, word) => {
        const prev = textInput.text;
        textInput.style = unwrappedStyle;
        textInput.text = word;
        const measured = textInput.width;
        textInput.text = prev;
        textInput.style = style;
        return Math.max(max, measured);
      }, 0);
      const requiredWidth = Math.min(maxWidth, Math.max(minWidth, longestWordWidth + padding * 2));
      if (requiredWidth > boxWidth) {
        boxWidth = requiredWidth;
        selectedText.boxWidth = boxWidth;
        onUpdateText(selectedText.id, { boxWidth });
      }

      textInput.style.wordWrapWidth = boxWidth - padding * 2;
      const textHeight = Math.max(38, textInput.height);
      const boxHeight = textHeight + padding * 2;

      if (selectedText.styleType === 'bubble') {
        bubbleBackground.clear();
        bubbleBackground.beginFill(0xffffff, 0.94);
        bubbleBackground.lineStyle(2, 0x6a58a5, 0.85);
        bubbleBackground.drawRoundedRect(0, 0, boxWidth, boxHeight, 16);
        bubbleBackground.moveTo(28, boxHeight - 4);
        bubbleBackground.lineTo(16, boxHeight + 14);
        bubbleBackground.lineTo(44, boxHeight - 4);
        bubbleBackground.endFill();
        bubbleBackground.visible = true;
      } else {
        bubbleBackground.visible = false;
      }

      textInputBackground.clear();
      textInputBackground.beginFill(0x787878, 0.25);
      textInputBackground.lineStyle(2, isLocallySelected ? 0xffff00 : 0x4f4f4f, 1);
      textInputBackground.drawRoundedRect(0, 0, boxWidth, boxHeight, 16);
      textInputBackground.endFill();

      const handleHeight = 26;
      resizeHandle.clear();
      resizeHandle.beginFill(0x36cedc, 0.95);
      resizeHandle.drawRoundedRect(boxWidth - 12, boxHeight / 2 - handleHeight / 2, 8, handleHeight, 4);
      resizeHandle.endFill();
      resizeHandle.visible = isLocallySelected;
      resizeHandle.interactive = isLocallySelected;
      resizeHandle.eventMode = isLocallySelected ? 'static' : 'none';
      textInputFieldContainer.hitArea = new Rectangle(0, 0, boxWidth, boxHeight);
    };

    adjustTextBoxSize();

    textInput.inputElement?.addEventListener('focus', startEditing);
    textInput.inputElement?.addEventListener('focusout', stopEditing);
    textInput.inputElement?.addEventListener('input', adjustTextBoxSize);

    textInputFieldContainer.interactive = true;
    textInputFieldContainer.eventMode = 'static';
    textInputFieldContainer.cursor = 'text';

    let isDragging = false;
    let isResizing = false;
    let dragOffset = { x: 0, y: 0 };
    let hasMoved = false;
    let resizeStartX = 0;
    let resizeStartWidth = boxWidth;

    resizeHandle.cursor = 'ew-resize';
    resizeHandle.on('pointerdown', (event) => {
      event.stopPropagation();
      selectText();
      isResizing = true;
      hasMoved = false;
      resizeStartX = event.data.global.x;
      resizeStartWidth = boxWidth;
      textInputBackground.visible = true;
      textInputFieldContainer.alpha = 1;
    });

    textInputFieldContainer.on('pointerdown', (event) => {
      if (isResizing) return;
      selectText();
      isDragging = true;
      hasMoved = false;
      const localPosition = event.data.getLocalPosition(textInputFieldContainer);
      dragOffset = { x: localPosition.x, y: localPosition.y };
      textInputFieldContainer.alpha = 0.8;
    });

    textInputFieldContainer.on('pointermove', (event) => {
      if (isResizing) {
        hasMoved = true;
        const delta = event.data.global.x - resizeStartX;
        boxWidth = Math.max(180, Math.min(900, resizeStartWidth + delta));
        selectedText.boxWidth = boxWidth;
        onUpdateText(selectedText.id, { boxWidth });
        adjustTextBoxSize();
        return;
      }

      if (isDragging && textInputFieldContainer.parent) {
        hasMoved = true;
        const newPosition = event.data.getLocalPosition(textInputFieldContainer.parent);
        textInputFieldContainer.x = newPosition.x - dragOffset.x;
        textInputFieldContainer.y = newPosition.y - dragOffset.y;
        selectedText.position.x = textInputFieldContainer.x;
        selectedText.position.y = textInputFieldContainer.y;
        onUpdateText(selectedText.id, {
          position: {
            x: textInputFieldContainer.x,
            y: textInputFieldContainer.y,
          },
        });
        if (
          isRecordingRef.current &&
          recordingStartTimeRef.current !== null &&
          selectedMenuRef.current === 'animations'
        ) {
          startRecording(event, textRef, selectedText.id, app, dragOffset);
        }
      }
    });

    textInputFieldContainer.on('pointerup', () => {
      if (isResizing) {
        isResizing = false;
        hasMoved = false;
        textInputBackground.visible = false;
        return;
      }
      isDragging = false;
      textInputFieldContainer.alpha = 1;
      restorePlaceholderIfEmpty();
      if (!hasMoved) startEditing();
      hasMoved = false;
    });

    textInputFieldContainer.on('pointerupoutside', () => {
      if (isResizing) {
        isResizing = false;
        hasMoved = false;
        textInputBackground.visible = false;
      }
      isDragging = false;
      textInputFieldContainer.alpha = 1;
      restorePlaceholderIfEmpty();
      hasMoved = false;
    });

    const onStagePointerDown = (event: any) => {
      const target = event?.target;
      if (!target) return;
      let node = target;
      let clickedInside = false;
      while (node) {
        if (node === textInputFieldContainer) {
          clickedInside = true;
          break;
        }
        node = node.parent;
      }
      if (!clickedInside) {
        isLocallySelected = false;
        textRefObject.isSelected = false;
        adjustTextBoxSize();
      }
    };
    app.stage.on('pointerdown', onStagePointerDown);

    const textRefObject: TextRef = {
      id: selectedText.id,
      textInput,
      textBackground: textInputBackground,
      textContainer: textInputFieldContainer,
      isDragging,
      recordedMovements: [],
      pathPoints: [],
      pathCircles: [],
      isSelected: false,
      position: { x: textInputFieldContainer.x, y: textInputFieldContainer.y },
      scale: 1,
      destroy: () => {
        textInput.inputElement?.removeEventListener('focus', startEditing);
        textInput.inputElement?.removeEventListener('focusout', stopEditing);
        textInput.inputElement?.removeEventListener('input', adjustTextBoxSize);
        textInputFieldContainer.removeAllListeners();
        app.stage.off('pointerdown', onStagePointerDown);
        app.stage.removeChild(textInputFieldContainer);
        textInputFieldContainer.destroy({ style: true });
      },
    };

    textRef?.current.set(selectedText.id, textRefObject);
    return textInputFieldContainer;
  } catch (error) {
    console.error('Error rendering text:', error);
  }
};
